import React, { useState, useEffect, useMemo, useRef } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import "../index.css";
import buttonBackground from "../assets/button_background_blue.webp";
import {
  type TreeNode,
  type CheckedItems,
  type IndeterminateItems,
  buildPlayersByTeam,
  augmentTreeWithPlayers,
  getDescendantIds,
  expandSelection,
  collectMinimalSelection,
  recomputeTreeState,
} from "./teamTree";

type ExpandedItems = Record<string, boolean>;

interface ValidationError {
  title: string;
  message: string;
}

interface CheckboxTreeProps {
  sportsMetadata: Record<string, unknown>;
  formState: number;
  setFormState: React.Dispatch<React.SetStateAction<number>>;
  teams: { [key: string]: boolean };
  setTeams: (teams: string[]) => void;
  ruleType?: number;
  playerMetadata?: Record<string, unknown>;
}

// Custom Checkbox component for React web
interface CheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  onPress: () => void;
}

const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  indeterminate,
  onPress,
}) => {
  return (
    <button
      onClick={onPress}
      style={{
        padding: 0,
        marginRight: 10,
        background: "none",
        border: "none",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          width: 20,
          height: 20,
          borderWidth: 2,
          borderStyle: "solid",
          borderRadius: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderColor: checked || indeterminate ? "#007AFF" : "#ccc",
          backgroundColor: checked || indeterminate ? "#007AFF" : "#fff",
        }}
      >
        {checked && !indeterminate && (
          <span
            style={{
              color: "#fff",
              fontSize: 12,
              fontWeight: "bold",
            }}
          >
            ✓
          </span>
        )}
        {indeterminate && (
          <span
            style={{
              color: "#fff",
              fontSize: 12,
              fontWeight: "bold",
            }}
          >
            -
          </span>
        )}
      </div>
    </button>
  );
};

// Main checkbox tree component
const CheckboxTree: React.FC<CheckboxTreeProps> = ({
  sportsMetadata,
  formState,
  setFormState,
  teams,
  setTeams,
  ruleType,
  playerMetadata,
}) => {
  const isPlayerMode = ruleType === 5;

  const treeData = useMemo((): TreeNode[] => {
    const baseNodes = sportsMetadata as unknown as TreeNode[];
    if (!isPlayerMode || !playerMetadata || Object.keys(playerMetadata).length === 0) {
      return baseNodes;
    }
    const playersByTeam = buildPlayersByTeam(playerMetadata);
    const augmented = augmentTreeWithPlayers(baseNodes, playersByTeam);
    return augmented;
  }, [sportsMetadata, isPlayerMode, playerMetadata]);

  const treeDataRef = useRef<TreeNode[]>(treeData);
  treeDataRef.current = treeData;

  const [checkedItems, setCheckedItems] = useState<CheckedItems>(() =>
    expandSelection(teams, treeData),
  );
  const [indeterminateItems, setIndeterminateItems] =
    useState<IndeterminateItems>({});
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    () => {
      const result: ExpandedItems = {};
      const init = (nodes: TreeNode[]) => {
        nodes.forEach((node) => {
          if (node.children && node.children.length > 0) {
            result[node.id] = node.id === "ALL";
            init(node.children);
          }
        });
      };
      init(treeData);
      return result;
    },
  );
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);

  // Handle checkbox change
  const handleCheckboxChange = (node: TreeNode, isChecked: boolean): void => {
    // Create a new checked items state
    const newCheckedItems = { ...checkedItems };

    // Get all descendant IDs
    const descendantIds = getDescendantIds(node);

    // Update checked status for this node and all its descendants
    descendantIds.forEach((id) => {
      newCheckedItems[id] = isChecked;
    });

    setCheckedItems(newCheckedItems);

    // Update indeterminate state and parent checked state
    updateIndeterminateState(newCheckedItems);
  };

  // Update indeterminate state and parent checked state
  const updateIndeterminateState = (checkedState: CheckedItems): void => {
    const { checked, indeterminate } = recomputeTreeState(
      treeDataRef.current,
      checkedState,
    );
    setCheckedItems(checked);
    setIndeterminateItems(indeterminate);
  };

  // Initialize indeterminate state
  useEffect(() => {
    const handleUpdate = (): void => {
      const newCheckedItems = { ...checkedItems };
      updateIndeterminateState(newCheckedItems);
    };

    handleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle node expansion
  const toggleNodeExpansion = (nodeId: string): void => {
    setExpandedItems((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  const handlePrevious = () => {
    setFormState(formState - 1);
  };

  const handleNext = () => {
    const minimalSelection = collectMinimalSelection(
      treeData,
      checkedItems,
      indeterminateItems,
    );

    if (minimalSelection.length === 0) {
      setValidationError({
        title: isPlayerMode ? "Player Selection Required" : "Team Selection Required",
        message: isPlayerMode
          ? "Please select at least one player to continue."
          : "Please select at least one team to continue.",
      });
      return;
    }

    setTeams(minimalSelection);
    setFormState(formState + 1);
  };

  // Recursive component to render each node
  interface TreeNodeComponentProps {
    node: TreeNode;
    level?: number;
  }

  const TreeNodeComponent: React.FC<TreeNodeComponentProps> = ({
    node,
    level = 0,
  }) => {
    const isChecked = !!checkedItems[node.id];
    const isIndeterminate = !!indeterminateItems[node.id];
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedItems[node.id] !== false; // Default to expanded

    return (
      <div style={{ marginBottom: 5, marginLeft: level === 0 ? 0 : 20 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            // paddingTop: 5,
            // paddingBottom: 5,
          }}
        >
          {hasChildren ? (
            <button
              onClick={() => toggleNodeExpansion(node.id)}
              style={{
                padding: 5,
                marginRight: 5,
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              {isExpanded ? (
                <ChevronDown size={16} color="#FFF" />
              ) : (
                <ChevronRight size={16} color="#FFF" />
              )}
            </button>
          ) : (
            <div style={{ width: 26, marginRight: 5, flexShrink: 0 }} />
          )}

          <Checkbox
            checked={isChecked}
            indeterminate={isIndeterminate}
            onPress={() => handleCheckboxChange(node, !isChecked)}
          />

          <button
            onClick={() => handleCheckboxChange(node, !isChecked)}
            style={{
              flex: 1,
              background: "none",
              border: "none",
              cursor: "pointer",
              textAlign: "left",
              padding: 0,
            }}
          >
            <span
              className="font_family_base detail_text"
              style={{
                fontSize: 16,
                // color: "#FFF",
              }}
            >
              {node.name}
            </span>
          </button>
        </div>

        {hasChildren && isExpanded && (
          <div>
            {node.children!.map((child) => (
              <TreeNodeComponent
                key={child.id}
                node={child}
                level={level + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Initialize expanded state for all parent nodes
  useEffect(() => {
    const initialExpandedState: ExpandedItems = {};

    const initializeExpandedState = (nodes: unknown): void => {
      (nodes as TreeNode[]).forEach((node: TreeNode) => {
        if (node.children && node.children.length > 0) {
          initialExpandedState[node.id] = node.id === "ALL"; // Set all nodes to expanded by default
          initializeExpandedState(node.children);
        }
      });
    };

    initializeExpandedState(treeData);
    setExpandedItems(initialExpandedState);
  }, [treeData]);

  return (
    <>
      <div
        style={{
          marginTop: "10px",
          padding: "0 10px",
          maxWidth: 800,
          margin: "0 auto",
        }}
      >
        <div className=" border_radius" style={{}}>
          <div className="center_me">
            <div
              className=" border_radius center_me"
              style={{
                // backgroundColor: "#2a2a2a",
                // borderRadius: 8,
                marginTop: 10,
                paddingLeft: 15,
                paddingRight: 15,
              }}
            >
              <h2
                className="font_family_base text_green center_me "
                style={{
                  fontSize: 20,
                  fontWeight: "bold",
                  marginBottom: 10,
                  textAlign: "center",
                  marginTop: 8,
                }}
              >
                {isPlayerMode ? "Select Players to Follow" : "Select Teams to Follow"}
              </h2>
            </div>
          </div>
          <div style={{ padding: 20 }}>
            {treeData.map((node: TreeNode) => (
              <TreeNodeComponent key={node.id} node={node} />
            ))}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            gap: 16,
            paddingLeft: 20,
            paddingRight: 20,
            paddingTop: 20,
            paddingBottom: 20,
          }}
        >
          <button
            className="my-form-button button-base button-3d font_family_base"
            style={{
              marginTop: "5px",
              marginBottom: "5px",
              backgroundImage: `url(${buttonBackground})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              color: "white",
              fontWeight: 700,
            }}
            onClick={handlePrevious}
          >
            ← Previous
          </button>
          <button
            className="my-form-button button-base button-3d font_family_base"
            style={{
              marginTop: "5px",
              marginBottom: "5px",
              backgroundImage: `url(${buttonBackground})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              color: "white",
              fontWeight: 700,
            }}
            onClick={handleNext}
          >
            Next →
          </button>
        </div>
      </div>

      {/* Validation Error Modal */}
      {validationError && (
        <div
          onClick={() => setValidationError(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="background_2 border_radius"
            style={{
              padding: 30,
              maxWidth: 400,
              width: "90%",
              textAlign: "center",
              border: "1px solid #333",
            }}
          >
            <h3
              className="font_family_base"
              style={{
                color: "#fff",
                marginBottom: 15,
                fontSize: 20,
              }}
            >
              {validationError.title}
            </h3>
            <p
              className="font_family_base"
              style={{
                color: "#ccc",
                marginBottom: 25,
                fontSize: 16,
                lineHeight: 1.5,
              }}
            >
              {validationError.message}
            </p>
            <button
              onClick={() => setValidationError(null)}
              className="my-form-button-wide button-base button-3d font_family_base"
              style={{
                backgroundImage: `url(${buttonBackground})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                color: "white",
                fontWeight: 700,
                padding: "12px 24px",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CheckboxTree;
